import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Chip, Divider, Grid, Typography } from "@mui/material";
import { RecieptsSectionContainer, HeaderBlock, RecieptTableContainer, RecieptsContainer } from "./styles";
import CustomTablePagination from "../../../../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { CUSTOM_PAGINATION_ID } from "../../../../../../reusable-components/Table/CustomTablePagination/utils";
import { HEADER_BLOCK_CLASSNAME, filterTravelExpenses } from "./utils";
import { SortByQuickfilter } from "../../../../../../containers/Quickfilters/SortByQuickfilter/SortByQuickfilter";
import { useSortByQuickFilter } from "../../../../../../containers/Quickfilters/SortByQuickfilter/useSortByQuickFilter";
import { TravelExpenseRef } from "../../../../../../models/travel-expenses";
import { Page } from "../../../../../../models/api/page";
import TravelExpenseReciept from "./TravelExpenseReciept/TravelExpenseReciept";
import { ExpenseReportView } from "../../../utils";
import AddEditTravelExpense from "./TravelExpenseReciept/AddEditTravelExpense/AddEditTravelExpense";
import { useAppSelector } from "../../../../../../custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "../../../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../../../utils/constants/authorization-constants";
import AuditComments from "./TravelExpenseReciept/AuditNotesActions/AuditComments/AuditComments";
import { EXPENSE_REPORT_STATUS } from "../../../../../../utils/constants/constants";
import { ROW_CLASSNAME } from "../../../../../../utils/export-expense-reports-utils/export-expense-reports-as-pdf";
import { Props, State } from "./interfaces";
import ExportedTravelExpenseReceipt from "./TravelExpenseReciept/ExportedTravelExpenseReceipt/ExportedTravelExpenseReceipt";
import { TravelExpenseDTO } from "src/models/travel-expenses/travel-expense-dto";
import { useTranslation } from "react-i18next";
const RecieptsSection = ({
  travelExpenses,
  expenseReportStatusId,
  view,
  handleChange,
  employee,
  handleNotifyParent,
  readOnly,
  wholeReportIsReadOnly,
  handleUpdateAuditActions,
  handleChangeAuditMutatedStatus,
  hideAuditNotes,
  customTableHeight,
  preloadedFlags,
  forExport,
  currUserAuth,
  allAuditNotes = [],
  isManagedByLink,
  handleUpdateExportAuditNotes,
}: Props) => {
  const tRP = "components.add_edit_expense_report";
  const { t } = useTranslation();

  const { sortByQuickFilter, setSortByQuickFilter } = useSortByQuickFilter();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAuditor = useMemo(() => {
    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.AuditTravelAndExpenses],
      currUserAuth ?? currentUserAuthorizations,
      employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, employee?.groups, currUserAuth]);

  const disabledAuditActions = useMemo(() => {
    return (
      wholeReportIsReadOnly ||
      expenseReportStatusId === EXPENSE_REPORT_STATUS.rejected.id ||
      expenseReportStatusId === EXPENSE_REPORT_STATUS.approved.id ||
      expenseReportStatusId === EXPENSE_REPORT_STATUS.cancelled.id ||
      expenseReportStatusId === EXPENSE_REPORT_STATUS.paid.id ||
      expenseReportStatusId === EXPENSE_REPORT_STATUS.returned_to_employee.id
    );
  }, [expenseReportStatusId, wholeReportIsReadOnly]);

  const [state, setState] = useState<State>({
    travelExpenses: [],
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
    diffHeight: 0,
  });

  const handleSetPage = (field: keyof Page, value: Page[keyof Page]): void =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  useLayoutEffect(() => {
    const paginationHeight = document.getElementById(`${CUSTOM_PAGINATION_ID}`)?.clientHeight ?? 0;
    const headerBlockHeight = document.getElementById(`${HEADER_BLOCK_CLASSNAME}`)?.clientHeight ?? 0;

    setState((state) => ({
      ...state,
      diffHeight: paginationHeight + headerBlockHeight,
    }));
  }, []);

  const handleSaveTravelExpenses = useCallback(
    (travelExpenseToSave: TravelExpenseDTO, effect: "add" | "edit", auditActionsMutated?: boolean) => {
      const travelExpensesToSave = [...travelExpenses];

      if (effect === "add") {
        travelExpensesToSave.unshift(travelExpenseToSave);
      }

      if (effect === "edit") {
        const recieptIdx = travelExpensesToSave.findIndex((el) => el?.id === travelExpenseToSave?.id);

        recieptIdx > -1 && travelExpensesToSave.splice(recieptIdx, 1, travelExpenseToSave);
      }

      handleChange && handleChange("travelExpenses", travelExpensesToSave);

      !!auditActionsMutated && handleChangeAuditMutatedStatus && handleChangeAuditMutatedStatus();
    },
    [travelExpenses, handleChange, handleChangeAuditMutatedStatus]
  );

  const filteredTravelExpenses: TravelExpenseDTO[] = useMemo(() => {
    return filterTravelExpenses(travelExpenses, state.page, sortByQuickFilter);
  }, [travelExpenses, state.page, sortByQuickFilter]);

  return (
    <RecieptsSectionContainer flex={1}>
      {!forExport && (
        <HeaderBlock id={HEADER_BLOCK_CLASSNAME}>
          <SortByQuickfilter
            quickFilters={sortByQuickFilter}
            handleChangeQuickFilters={setSortByQuickFilter}
            isExpenseReportOrReceipt={"receipt"}
          />

          {view === ExpenseReportView.employee && !readOnly && !wholeReportIsReadOnly && (
            <AddEditTravelExpense
              effect="add"
              employee={employee}
              handleSaveTravelExpense={(travelExpense) => handleSaveTravelExpenses(travelExpense, "add")}
              view={view}
              expenseReportStatusId={expenseReportStatusId}
            />
          )}
        </HeaderBlock>
      )}

      <>
        {!forExport && (
          <RecieptTableContainer className={ROW_CLASSNAME}>
            <RecieptsContainer
              diffHeight={state.diffHeight}
              noItems={!filteredTravelExpenses.length}
              customTableHeight={customTableHeight}
            >
              <>
                {!!filteredTravelExpenses.length &&
                  filteredTravelExpenses.map((travelExpense) => {
                    return (
                      <React.Fragment key={travelExpense.id}>
                        {!isAuditor && (
                          <AddEditTravelExpense
                            effect="edit"
                            item={travelExpense}
                            employee={employee}
                            handleSaveTravelExpense={(travelExpense, auditActionsMutated) =>
                              handleSaveTravelExpenses(travelExpense, "edit", auditActionsMutated)
                            }
                            view={view}
                            readOnly={
                              readOnly ||
                              wholeReportIsReadOnly ||
                              travelExpense.auditAccepted !== null ||
                              travelExpense.compliantReceipt
                            }
                            disabledAuditActions={disabledAuditActions}
                            handleUpdateAuditActions={handleUpdateAuditActions}
                            hideAuditNotes={hideAuditNotes}
                            expenseReportStatusId={expenseReportStatusId}
                            isManagedByLink={isManagedByLink}
                            handleUpdateExportAuditNotes={handleUpdateExportAuditNotes}
                          >
                            <TravelExpenseReciept
                              travelExpense={travelExpense}
                              expenseReportStatusId={expenseReportStatusId}
                              view={view}
                              handleChange={handleChange}
                              allTravelExpenses={travelExpenses}
                              readOnly={readOnly || wholeReportIsReadOnly}
                              employee={employee}
                              handleNotifyParent={handleNotifyParent}
                              hideAuditNotes={hideAuditNotes}
                            />
                          </AddEditTravelExpense>
                        )}

                        {isAuditor && !hideAuditNotes && (
                          <AuditComments
                            item={travelExpense}
                            employee={employee}
                            handleNotifyParent={handleNotifyParent}
                            handleUpdateAuditActions={handleUpdateAuditActions}
                            readOnly={disabledAuditActions}
                            handleUpdateExportAuditNotes={handleUpdateExportAuditNotes}
                          >
                            <TravelExpenseReciept
                              travelExpense={travelExpense}
                              expenseReportStatusId={expenseReportStatusId}
                              view={view}
                              handleChange={handleChange}
                              allTravelExpenses={travelExpenses}
                              readOnly={readOnly || wholeReportIsReadOnly}
                              employee={employee}
                              handleNotifyParent={handleNotifyParent}
                              hideAuditNotes={hideAuditNotes}
                            />
                          </AuditComments>
                        )}
                      </React.Fragment>
                    );
                  })}

                {!filteredTravelExpenses.length && <Typography>{`${t(`${tRP}.no_receipts`)}`}</Typography>}
              </>
            </RecieptsContainer>

            <Divider orientation="horizontal" />

            <CustomTablePagination
              totalItems={travelExpenses.length}
              pageNumber={state.page.pageNumber}
              handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
              pageSize={state.page.pageSize}
              handleChangePageSize={(s) => {
                handleSetPage("pageSize", s);
                handleSetPage("pageNumber", 0);
              }}
            />
          </RecieptTableContainer>
        )}
      </>

      <>
        {forExport && (
          <Grid sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
            <Divider
              orientation="horizontal"
              style={{
                alignItems: "start",
                marginBottom: "20px",
              }}
            >
              <Chip label={`Receipts (${travelExpenses.length})`} />
            </Divider>
            <>
              {!!travelExpenses.length &&
                travelExpenses.map((travelExpense) => {
                  return (
                    <Grid
                      className={ROW_CLASSNAME}
                      key={travelExpense.id}
                    >
                      <ExportedTravelExpenseReceipt
                        isAuditor={isAuditor}
                        travelExpense={travelExpense}
                        expenseReportStatusId={expenseReportStatusId}
                        view={view}
                        allTravelExpenses={travelExpenses}
                        readOnly={readOnly || wholeReportIsReadOnly}
                        employee={employee}
                        hideAuditNotes={hideAuditNotes}
                        preloadedFlags={preloadedFlags}
                        allAuditNotes={allAuditNotes.filter(
                          (auditNote) => auditNote.travelExpense?.id === travelExpense.id
                        )}
                      />
                    </Grid>
                  );
                })}

              {!travelExpenses.length && <Typography>{`${t(`${tRP}.no_receipts`)}`}</Typography>}
            </>
          </Grid>
        )}
      </>
    </RecieptsSectionContainer>
  );
};

export default React.memo(RecieptsSection);
